/**
 * OneProGear — small behaviours the CSS cannot express.
 *
 * 1. The phone sticky CTA gets out of the way once the footer is on screen, so
 *    it never sits on top of the footer links (REFINE-BRIEF A.4). The bar is
 *    `position: fixed`, so nothing but JS can know when the footer arrives.
 * 2. Rails (`.opg-rail`) get their scroll affordance: a fade on whichever side
 *    still has cards off screen. CSS cannot read scroll position, so the state
 *    lives in data attributes and `assets/opg-theme.css` draws the mask.
 * 3. OPG prices follow the selected variant. Horizon's variant picker fires
 *    `shopify:product:select` and re-renders the product section; it only morphs
 *    its own elements, so the buy-box price and the sticky-CTA price
 *    (`[data-opg-live-price]`, snippets/opg-price.liquid) are copied from that
 *    same response here. Without this the PDP kept the first variant's price
 *    while the cart charged the selected one (fixed 2026-09-24).
 *
 * No libraries, no dependencies, safe to load with `defer`.
 */
(() => {
  'use strict';

  /* -- 1. sticky CTA vs footer -------------------------------------------- */
  const bar = document.querySelector('.opg-sticky-cta');
  const footer = document.querySelector('footer');

  if (bar && footer && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            bar.setAttribute('data-opg-hidden', '');
          } else {
            bar.removeAttribute('data-opg-hidden');
          }
        }
      },
      // A sliver of the footer is enough — hide before the overlap happens.
      { rootMargin: '0px 0px -8px 0px', threshold: 0 }
    );

    observer.observe(footer);
  }

  /* -- 2. rail scroll affordance ------------------------------------------ */
  const rails = document.querySelectorAll('.opg-rail');
  if (!rails.length) return;

  const update = (rail) => {
    // 2px of slack: sub-pixel track widths make an exact comparison flicker.
    const overflow = rail.scrollWidth - rail.clientWidth > 2;
    rail.toggleAttribute('data-opg-overflow', overflow);
    if (!overflow) {
      rail.removeAttribute('data-opg-at-start');
      rail.removeAttribute('data-opg-at-end');
      return;
    }
    const start = Math.abs(rail.scrollLeft) <= 2;
    const end = Math.abs(rail.scrollLeft) + rail.clientWidth >= rail.scrollWidth - 2;
    rail.toggleAttribute('data-opg-at-start', start);
    rail.toggleAttribute('data-opg-at-end', end);
  };

  const updateAll = () => {
    for (const rail of rails) update(rail);
  };

  for (const rail of rails) {
    update(rail);
    rail.addEventListener('scroll', () => update(rail), { passive: true });
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => update(rail)).observe(rail);
    }
  }

  window.addEventListener('resize', updateAll);

  // Card images decide the real scrollWidth and they arrive late, without
  // resizing the rail's own box — so ResizeObserver never fires. Sweep again
  // once layout has settled, and whenever an image inside a rail finishes.
  requestAnimationFrame(updateAll);
  window.addEventListener('load', updateAll);
  document.addEventListener('load', (e) => {
    if (e.target instanceof HTMLImageElement && e.target.closest('.opg-rail')) updateAll();
  }, true);
})();

/* -- 0. Rupee formatter ------------------------------------------------------
 * Same rule as snippets/opg-money.liquid: paise in, "₹3,34,530" out (Indian
 * grouping, no decimals). Independent of the shop's money format string.
 */
window.OPG = window.OPG || {};
window.OPG.formatINR = (paise) => {
  const n = Math.round((Number(paise) || 0) / 100);
  const neg = n < 0;
  const d = String(Math.abs(n));
  let out = d;
  if (d.length > 3) {
    const head = d.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    out = `${head},${d.slice(-3)}`;
  }
  return `${neg ? '-' : ''}₹${out}`;
};

/* -- 3. OPG prices follow the selected variant ------------------------------ */
(() => {
  'use strict';

  if (!document.querySelector('[data-opg-live-price]')) return;

  // Fallback when the section response carries no OPG price block: redraw the
  // amount from the variant JSON with the shared formatter.
  const applyVariant = (variant, productId) => {
    if (!variant || variant.price == null) return;
    const paise = typeof variant.price === 'object' ? variant.price.amount * 100 : variant.price;
    for (const el of document.querySelectorAll(`[data-opg-live-price][data-product-id="${productId}"]`)) {
      const amount = el.querySelector('.opg-price__amount');
      if (amount) amount.textContent = window.OPG.formatINR(paise);
      el.dataset.variantId = String(variant.id || '');
    }
  };

  const apply = (html, productId, variant) => {
    if (!html || typeof html.querySelector !== 'function') return applyVariant(variant, productId);
    const fresh = html.querySelector(
      `[data-opg-live-price][data-product-id="${productId}"]:not(.opg-price--card)`
    ) || html.querySelector(`[data-opg-live-price][data-product-id="${productId}"]`);
    if (!fresh) return applyVariant(variant, productId);
    for (const el of document.querySelectorAll(`[data-opg-live-price][data-product-id="${productId}"]`)) {
      if (el.innerHTML !== fresh.innerHTML) el.innerHTML = fresh.innerHTML;
      el.dataset.variantId = fresh.dataset.variantId || '';
    }
  };

  // Capture phase on document: works whether or not the event bubbles.
  document.addEventListener(
    'shopify:product:select',
    (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('product-card, dialog')) return;
      const promise = event.promise || (event.detail && event.detail.promise);
      if (!promise || typeof promise.then !== 'function') return;
      promise
        .then((result) => {
          const detail = result && result.detail;
          if (!detail) return;
          const productId =
            (detail.newProduct && detail.newProduct.id) ||
            detail.productId ||
            (target instanceof Element && target.closest('[data-product-id]')?.dataset.productId);
          if (productId) apply(detail.html, String(productId), detail.resource || detail.variant);
        })
        .catch(() => {});
    },
    true
  );
})();
